import { t as tr } from '../i18n.js'
import { useRef, useState } from 'react'
import { Download, FileText, Paperclip } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, uploadFile, type Attachment } from '../api.js'
import { fmt } from './Account.js'
import type { CommentEntity } from './Comments.js'

/**
 * Files on a task (or a project / milestone): a list with image thumbnails, a
 * drop zone and an "Attach files" button. Files embedded in a comment show up
 * here too, marked as such, so the task's list is the one place to find them.
 */
export const attachmentsUrl = (projectId: string, e: CommentEntity) =>
  e.type === 'task' ? `/projects/${projectId}/tasks/${e.id}/attachments`
    : e.type === 'milestone' ? `/projects/${projectId}/milestones/${e.id}/attachments`
      : `/projects/${projectId}/attachments`
export const attachmentsKey = (e: CommentEntity) => ['attachments', e.type, e.id]

/** 512 B, 3.4 KB, 12 MB. */
export const fmtBytes = (n: number) => (n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${Math.round(n / 102.4) / 10} KB` : `${Math.round(n / (1024 * 102.4)) / 10} MB`)

/** The Markdown a comment carries for a file: images show, anything else is a link. */
export const attachmentMarkdown = (a: Attachment) => `${a.image ? '!' : ''}[${a.fileName.replace(/[[\]]/g, '')}](${a.url})`

/** Upload files one after another, refreshing the entity's list; resolves to what was stored. */
export function useUpload(projectId: string, entity: CommentEntity, onError: (e: unknown) => void) {
  const qc = useQueryClient()
  const [busy, setBusy] = useState(0)
  const upload = async (files: File[]): Promise<Attachment[]> => {
    const done: Attachment[] = []
    setBusy((n) => n + files.length)
    try {
      for (const f of files) {
        try { done.push(await uploadFile(attachmentsUrl(projectId, entity), f)) } catch (e) { onError(e) } finally { setBusy((n) => n - 1) }
      }
    } finally {
      void qc.invalidateQueries({ queryKey: attachmentsKey(entity) })
    }
    return done
  }
  return { upload, busy }
}

export function Attachments({ projectId, entity, canEdit }: { projectId: string; entity: CommentEntity; canEdit: boolean }) {
  const qc = useQueryClient()
  const key = attachmentsKey(entity)
  const q = useQuery({ queryKey: key, queryFn: () => api<{ attachments: Attachment[] }>(attachmentsUrl(projectId, entity)) })
  const [err, setErr] = useState<string | null>(null)
  const [over, setOver] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const onError = (e: unknown) => setErr(e instanceof ApiError ? e.message : tr("Upload failed"))
  const { upload, busy } = useUpload(projectId, entity, onError)
  const del = useMutation({
    mutationFn: (id: string) => api<null>(`/projects/${projectId}/attachments/${id}`, { method: 'DELETE' }),
    onSuccess: () => { setErr(null); void qc.invalidateQueries({ queryKey: key }) },
    onError,
  })
  const files = q.data?.attachments ?? []
  const send = (list: FileList | null) => { if (list?.length) { setErr(null); void upload(Array.from(list)) } }

  return (
    <div className={`stack attachments ${over ? 'dropover' : ''}`}
      onDragOver={canEdit ? (e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setOver(true) } } : undefined}
      onDragLeave={canEdit ? (e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false) } : undefined}
      onDrop={canEdit ? (e) => { if (e.dataTransfer.files.length) { e.preventDefault(); setOver(false); send(e.dataTransfer.files) } } : undefined}>
      <div className="row between">
        <strong className="small">{tr("Attachments")}{files.length ? ` · ${files.length}` : ''}</strong>
        {canEdit && (
          <>
            <button type="button" className="button sm" data-tip={tr("Attach files to this task; you can also drop them here")} onClick={() => input.current?.click()}>
              <Paperclip size={14} strokeWidth={2} aria-hidden /> {tr("Attach files")}
            </button>
            <input ref={input} type="file" multiple hidden onChange={(e) => { send(e.target.files); e.target.value = '' }} />
          </>
        )}
      </div>
      {q.isPending && <p className="muted small">{tr("Loading…")}</p>}
      {!q.isPending && files.length === 0 && <p className="muted small">{canEdit ? tr("No files yet. Drop files here or use Attach files.") : tr("No files.")}</p>}
      {files.length > 0 && (
        <ul className="plain attachlist">
          {files.map((a) => (
            <li key={a.id} className="attachment">
              <a className="athumb" href={a.url} target="_blank" rel="noopener" aria-hidden tabIndex={-1}>
                {a.image ? <img src={a.url} alt="" loading="lazy" /> : <FileText size={20} strokeWidth={1.75} aria-hidden />}
              </a>
              <div className="ainfo">
                <a href={a.url} target="_blank" rel="noopener" className="aname" title={a.fileName}>{a.fileName}</a>
                <span className="small muted">
                  {fmtBytes(a.sizeBytes)} · {a.uploadedByName ?? tr("system")} · {fmt(a.createdAt)}{a.commentId ? ` · ${tr("in a comment")}` : ''}
                </span>
              </div>
              <a className="link plain" href={`${a.url}?download=1`} aria-label={tr("Download")} data-tip={tr("Download")}>
                <Download size={14} strokeWidth={2} aria-hidden />
              </a>
              {canEdit && a.canDelete && (
                <button type="button" className="link small danger" data-tip={tr("Remove this file")}
                  onClick={() => { if (confirm(tr("Delete {name}?", { name: a.fileName }))) del.mutate(a.id) }}>{tr("Delete")}</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {busy > 0 && <p className="small muted">{busy === 1 ? tr("Uploading 1 file…") : tr("Uploading {n} files…", { n: busy })}</p>}
      {err && <p className="bad small">{err}</p>}
    </div>
  )
}
